import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "sonner";
import { approveKyc } from "../apis";
import { KycCheckError, listCustomerDocuments, listKycWaiting, uploadCustomerDocument, type KycCheckWaiting } from "service/moneeflo/legacy-forex-desk-api";
import FilePicker from "./FilePicker";
import DeskDialog, { Field, inputClass } from "./DeskDialog";

const CHECK_LABEL: Record<KycCheckWaiting, string> = { pan: "PAN", passport: "Passport" };
/** A request or a rejected or replaced document is not evidence that the papers were seen. */
const NOT_EVIDENCE = ["requested", "rejected", "superseded"];

/**
 * Approving a customer's KYC by hand. It is the fallback for when the automatic
 * check cannot be used (PLT-53), so it asks for why, and, for each check waiting
 * (PAN, passport), for the uploaded document that shows those papers were seen:
 * one already filed on the customer, or a new one filed on the customer here
 * (never on the order). Which types suit which check is core's rule
 * (`kyc.manual_evidence_types`); the page offers the uploaded, non-rejected documents whose
 * type is the check's own and shows core's refusal as it comes back.
 */
export default function ManualKycDialog({
  open,
  onOpenChange,
  customerId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
}) {
  const [reason, setReason] = useState("");
  const [evidenceIds, setEvidenceIds] = useState<Partial<Record<KycCheckWaiting, string>>>({});
  const [files, setFiles] = useState<Partial<Record<KycCheckWaiting, File | null>>>({});
  const [error, setError] = useState<string | null>(null);
  const [failedCheck, setFailedCheck] = useState<KycCheckWaiting | null>(null);
  // A file already filed on the customer is not filed again when the person retries.
  const uploaded = useRef<Partial<Record<KycCheckWaiting, { file: File; id: string }>>>({});
  const queryClient = useQueryClient();

  const { data: waiting } = useQuery({
    queryKey: ["kycWaiting", customerId],
    queryFn: () => listKycWaiting(customerId),
    enabled: open && !!customerId,
    cacheTime: 0,
  });
  const { data: documents } = useQuery({
    queryKey: ["customerDocuments", customerId],
    queryFn: () => listCustomerDocuments(customerId),
    enabled: open && !!customerId,
    cacheTime: 0,
  });
  const checks = Array.from(new Set(waiting ?? []));
  // Only an uploaded file, still in play, of the kind that proves this check (a PAN check takes a pan, a passport check a passport).
  const evidenceFor = (type: KycCheckWaiting) =>
    (documents ?? []).filter(
      (d) => !NOT_EVIDENCE.includes(String(d.status).toLowerCase()) && d.downloadable !== false && d.docType === type,
    );

  useEffect(() => {
    if (open) {
      setReason("");
      setEvidenceIds({});
      setFiles({});
      setError(null);
      setFailedCheck(null);
      uploaded.current = {};
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: async () => {
      const chosen: Partial<Record<KycCheckWaiting, string>> = {};
      for (const type of checks) {
        const file = files[type];
        if (file) {
          const done = uploaded.current[type];
          if (done && done.file === file) {
            chosen[type] = done.id;
          } else {
            const id = (await uploadCustomerDocument(customerId, file, type)).id;
            uploaded.current[type] = { file, id };
            chosen[type] = id;
          }
        } else {
          chosen[type] = evidenceIds[type];
        }
      }
      return approveKyc({ customers_id: customerId, reason, evidence: chosen });
    },
    onSuccess: () => {
      toast.success("KYC approved");
      queryClient.invalidateQueries(["orderDetails"]);
      queryClient.invalidateQueries(["kycLogs"]);
      onOpenChange(false);
    },
    onError: (e: unknown) => {
      setError(e instanceof Error ? e.message : "The KYC could not be approved.");
      setFailedCheck(e instanceof KycCheckError ? e.check : null);
      // A check that did pass is no longer waiting.
      queryClient.invalidateQueries(["kycWaiting", customerId]);
      queryClient.invalidateQueries(["kycLogs"]);
    },
  });

  const ready = reason.trim().length >= 3 && checks.length > 0 && checks.every((t) => !!evidenceIds[t] || !!files[t]);

  return (
    <DeskDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Approve KYC by hand"
      description="Use this when the automatic check cannot be used. Say why, and attach what shows each paper was seen."
      busy={mutation.isLoading}
      submitLabel="Approve KYC"
      submitDisabled={!ready}
      error={error}
      onSubmit={() => {
        setError(null);
        setFailedCheck(null);
        mutation.mutate();
      }}
    >
      <Field label="Reason" htmlFor="kyc-reason" hint="At least 3 characters. It is kept with the approval.">
        <textarea
          id="kyc-reason"
          className={`${inputClass} min-h-[72px]`}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </Field>
      {waiting && checks.length === 0 && (
        <p className="text-sm text-[#666666]">There is no PAN or passport waiting to be approved.</p>
      )}
      {checks.map((type) => (
        <div key={type} className={`flex flex-col gap-3 rounded-lg border p-3 ${failedCheck === type ? "border-[#B00020] bg-[#FFF8F8]" : "border-[#E5E5E5]"}`} data-testid={`kyc-evidence-${type}`}>
          <p className="text-sm font-semibold text-[#333333]">{CHECK_LABEL[type]} check</p>
          <Field label={`Evidence for the ${CHECK_LABEL[type]}`} htmlFor={`kyc-evidence-${type}`} hint="An uploaded document already filed on the customer.">
            <select
              id={`kyc-evidence-${type}`}
              className={inputClass}
              value={evidenceIds[type] ?? ""}
              disabled={!!files[type]}
              aria-invalid={failedCheck === type}
              onChange={(e) => setEvidenceIds((prev) => ({ ...prev, [type]: e.target.value }))}
            >
              <option value="">Choose a document</option>
              {evidenceFor(type).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.label || d.fileName || d.docType} ({d.status})
                </option>
              ))}
            </select>
          </Field>
          <Field label={`Or a new ${CHECK_LABEL[type]} document`} htmlFor={`kyc-evidence-file-${type}`} hint="Filed on the customer, not on this order.">
            <FilePicker
              id={`kyc-evidence-file-${type}`}
              file={files[type] ?? null}
              invalid={failedCheck === type}
              onChange={(file) => setFiles((prev) => ({ ...prev, [type]: file }))}
            />
          </Field>
        </div>
      ))}
    </DeskDialog>
  );
}
